import { useEffect, useRef, useState } from "react";
import { ApiError } from "@trillion/ui";
import { Button } from "@trillion/ui/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@trillion/ui/components/ui/card";
import { Alert, AlertDescription } from "@trillion/ui/components/ui/alert";
import { mcpConnectionsApi, type McpConnectionsView } from "../lib/mcp-connections-api";

export function ConnectedAgentsSection({ userId }: { userId: string }) {
  const [view, setView] = useState<McpConnectionsView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sessionLost, setSessionLost] = useState(false);
  const [message, setMessage] = useState("");
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [attempt, setAttempt] = useState(0);
  const requests = useRef(new Map<string, AbortController>());
  const generation = useRef(0);
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const current = ++generation.current;
    const controller = new AbortController();
    const mutations = requests.current;
    setView(null); setError(""); setMessage(""); setSessionLost(false); setPending(new Set()); setLoading(true);
    mcpConnectionsApi.list(controller.signal).then(result => {
      if (!Array.isArray(result?.connections) || typeof result.csrf_token !== "string")
        throw new Error("Invalid connection response");
      if (generation.current === current) setView(result);
    }).catch(failure => {
      if (generation.current !== current) return;
      const ended = failure instanceof ApiError && failure.status === 401;
      setSessionLost(ended);
      setError(ended ? "Your session ended. Sign in again to manage connections." : "Could not load connected agents. Try again.");
    }).finally(() => {
      if (generation.current === current) setLoading(false);
    });
    return () => {
      ++generation.current;
      controller.abort();
      mutations.forEach(request => request.abort()); mutations.clear();
    };
  }, [userId, attempt]);

  async function disconnect(authorizationId: string, connectionId: string) {
    if (!view || sessionLost || requests.current.has(authorizationId)) return;
    const current = generation.current;
    const controller = new AbortController();
    requests.current.set(authorizationId, controller);
    setPending(new Set(requests.current.keys())); setError(""); setMessage("");
    try {
      await mcpConnectionsApi.disconnect(authorizationId, view.csrf_token, controller.signal);
      if (generation.current !== current) return;
      setView(previous => previous && ({ ...previous, connections: previous.connections.filter(row => row.authorization_id !== authorizationId) }));
      setMessage(`Connection ${connectionId} disconnected.`);
      heading.current?.focus();
    } catch (failure) {
      if (generation.current !== current) return;
      const ended = failure instanceof ApiError && failure.status === 401;
      setSessionLost(ended);
      setError(ended ? "Your session ended. Sign in again to manage connections." : `Could not disconnect ${connectionId}. Try Disconnect again.`);
    } finally {
      if (generation.current === current) {
        requests.current.delete(authorizationId);
        setPending(new Set(requests.current.keys()));
      }
    }
  }

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle><h2 ref={heading} tabIndex={-1} className="focus-visible:outline-2 focus-visible:outline-ring">Connected agents</h2></CardTitle>
        <CardDescription>Manage each Board MCP connection separately.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">Disconnect stops future renewal. Already-issued access may continue for up to 11 minutes. Clearing credentials in an agent only removes its local copy; it does not revoke the Passport connection.</p>
        {loading && <p role="status">Loading connected agents…</p>}
        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
        {message && <p role="status" className="text-sm">{message}</p>}
        {!loading && !view && !sessionLost && <Button type="button" variant="outline" onClick={() => setAttempt(value => value + 1)}>Retry loading connections</Button>}
        {!loading && view?.connections.length === 0 && <p>No connected agents.</p>}
        {!loading && view && view.connections.length > 0 && (
          <ul className="space-y-4">
            {view.connections.map(connection => (
              <li key={connection.authorization_id} className="space-y-2 rounded-md border p-3">
                <div className="min-w-0 break-words">
                  <p className="font-medium">{connection.display_name}</p>
                  <p className="text-sm text-muted-foreground">{connection.client_id}</p>
                  <p className="text-sm">Connection {connection.connection_id}</p>
                  <p className="text-sm text-muted-foreground">Created <time dateTime={connection.created_at}>{new Date(connection.created_at).toLocaleString()}</time></p>
                </div>
                <Button type="button" variant="outline" disabled={sessionLost || pending.has(connection.authorization_id)}
                  aria-label={`Disconnect connection ${connection.connection_id}`}
                  onClick={() => disconnect(connection.authorization_id, connection.connection_id)}>
                  {pending.has(connection.authorization_id) ? "Disconnecting…" : "Disconnect"}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
